import {
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import { getBusinessRelationsForUser } from '../../../../../../../lib/api';
import {
  AdminUserRelation,
  useCreateUserRelation,
  useDeleteUserRelation,
  useGetAdminUserRelations,
  useToggleUserRelationActive,
} from '../../../../../../../lib/api/users';
import CustomButton from '../../../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../../../../lib/designSystem/Table';
import { showAlert } from '../../../../../../../lib/functions/utils';
import { CompanyRelations } from '../..';

export interface UserInterface {
  id: number;
  name: string;
  email?: string;
  permissionPolicy: string;
  password?: string;
  roles?: string[];
  active?: boolean;
  permissionPolicyCode?: number;
}

const EditUserBusinesses = (props: any) => {
  const {
    handleCloseDialog,
    openCompanyDialog,
    selectedUser,
    setCompanyRelations,
    companyRelations,
    setLoading,
  } = props;
  const dispatch = useDispatch();
  const [businessId, setBusinessId] = React.useState('');
  const [openAddCompany, setOpenAddCompany] = useState(false);
  const [loadingDropdown, setLoadingDropdown] = useState(false);

  const { data: userRelations = [], isFetching } = useGetAdminUserRelations(
    dispatch,
    selectedUser.id,
    openCompanyDialog,
  );

  const deleteMutation = useDeleteUserRelation(dispatch, selectedUser.id);
  const createMutation = useCreateUserRelation(dispatch, selectedUser.id);
  const toggleMutation = useToggleUserRelationActive(dispatch, selectedUser.id);

  const loading = isFetching || loadingDropdown;

  const handleChange = (event: SelectChangeEvent) => {
    setBusinessId(event.target.value);
  };

  const handleOpenAddCompany = async () => {
    setOpenAddCompany(true);
    setLoadingDropdown(true);
    const relations = await getBusinessRelationsForUser(dispatch);
    const businessIdentifiers = userRelations.map(
      (x: AdminUserRelation) => x.id,
    );
    const filteredRelations = relations.relations.filter(
      (x: CompanyRelations) => !businessIdentifiers.includes(String(x.id)),
    );
    setCompanyRelations(filteredRelations);
    setLoadingDropdown(false);
  };

  const handleSave = async () => {
    try {
      await createMutation.mutateAsync(businessId);
      showAlert(dispatch, 'success', 'Se ha agregado la asociación');
    } catch (error) {
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    }
    closeAdding();
  };

  const handleDeleteRelation = async (id: number) => {
    const relation = userRelations.find(
      (x: AdminUserRelation) => x.id === String(id),
    );
    if (!relation) return alert('Ha ocurrido un error');
    try {
      await deleteMutation.mutateAsync(String(relation.id));
      showAlert(dispatch, 'success', 'Se ha borrado la asociación');
    } catch (error) {
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    }
    closeAdding();
  };

  const handleToggleRelationActive = async (id: number, active: boolean) => {
    const relation = userRelations.find(
      (x: AdminUserRelation) => x.id === String(id),
    );
    if (!relation) return alert('Ha ocurrido un error');
    setLoading(true);
    try {
      await toggleMutation.mutateAsync({
        businessId: String(relation.id),
        active,
      });
      showAlert(
        dispatch,
        'success',
        active ? 'Se ha activado la relación' : 'Se ha desactivado la relación',
      );
    } catch (error) {
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    }
    setLoading(false);
  };

  const closeAdding = () => {
    setOpenAddCompany(false);
    setBusinessId('');
  };

  const canShowActivate = (id: number) => {
    const relation = userRelations.find(
      (x: AdminUserRelation) => x.id === String(id),
    );
    return relation ? !relation.userRelationActive && relation.active : false;
  };

  const canShowDeactivate = (id: number) => {
    const relation = userRelations.find(
      (x: AdminUserRelation) => x.id === String(id),
    );
    return relation ? relation.userRelationActive : false;
  };

  const actionsUserRelations = [
    { action: handleDeleteRelation, actionLabel: 'Desasociar' },
    {
      action: (id: number) => handleToggleRelationActive(id, true),
      actionLabel: 'Activar',
      canShow: canShowActivate,
    },
    {
      action: (id: number) => handleToggleRelationActive(id, false),
      actionLabel: 'Desactivar',
      canShow: canShowDeactivate,
    },
  ];

  const buttonsData: CustomButtonProps[] = [
    {
      action: () => handleOpenAddCompany(),
      name: 'Asociar Cliente',
      color: 'primary',
    },
  ];

  return (
    <div>
      <Dialog
        fullWidth
        open={openCompanyDialog}
        onClose={() => handleCloseDialog()}
      >
        {loading && <CenteredCircularProgress type="dialog" />}
        {!loading && (
          <div>
            <DialogContent>
              <HeaderWithActions
                type="subHeader"
                buttons={
                  selectedUser.permissionPolicy === 'Administrador'
                    ? []
                    : !openAddCompany
                      ? buttonsData
                      : []
                }
                title="Clientes visibles"
              />
              <Typography margin="8px 0px">
                Aqui puedes ver los clientes asociados a {selectedUser.name} (
                {selectedUser.email})
              </Typography>
              {!openAddCompany ? (
                <EnhancedTable
                  dataHeaders={[
                    { key: 'businessName', label: 'Cliente' },
                    {
                      key: 'active',
                      label: 'Estado Empresa',
                      type: 'boolean-active',
                    },
                    {
                      key: 'userRelationActive',
                      label: 'Estado Usuario',
                      type: 'boolean-active',
                    },
                  ]}
                  data={userRelations}
                  showActions={
                    !(selectedUser.permissionPolicy === 'Administrador')
                  }
                  rowsPerPageDefault={5}
                  actions={
                    selectedUser.permissionPolicy === 'Administrador'
                      ? []
                      : actionsUserRelations
                  }
                />
              ) : (
                <div>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">
                      Clientes disponibles
                    </InputLabel>
                    <Select
                      labelId="demo-simple-select-label"
                      id="demo-simple-select"
                      value={businessId}
                      label="Clientes disponibles"
                      onChange={(e) => handleChange(e)}
                    >
                      {companyRelations.map(
                        (companyRelation: CompanyRelations, index: number) => (
                          <MenuItem key={index} value={companyRelation.id}>
                            {companyRelation.businessName}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  </FormControl>
                  <div
                    style={{ textAlign: 'center', padding: '30px 0px 0px' }}
                  ></div>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <CustomButton
                onClick={() => handleCloseDialog()}
                color="secondary"
              >
                Salir
              </CustomButton>
              {openAddCompany && (
                <CustomButton onClick={closeAdding} color="secondary">
                  Cancelar
                </CustomButton>
              )}
              {openAddCompany && (
                <CustomButton
                  color="primary"
                  disabled={!businessId}
                  onClick={handleSave}
                >
                  Guardar
                </CustomButton>
              )}
            </DialogActions>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default EditUserBusinesses;
